import type {LayerSpecification, Map, SourceSpecification, StyleSpecification} from 'maplibre-gl';
import {MAP_PROVIDER_CONFIG} from './map-provider.config';

type MapMode = keyof typeof MAP_PROVIDER_CONFIG.styleUrls;

/** Keep only application-owned overlays; provider layers always come from the new style. */
export function preserveMapLayers(
  previous: StyleSpecification | undefined,
  next: StyleSpecification,
  sourceIds: readonly string[],
  layerIds: readonly string[],
): StyleSpecification {
  const sources: Record<string, SourceSpecification> = {...next.sources};
  sourceIds.forEach(id => {
    if (previous?.sources[id]) sources[id] = previous.sources[id];
  });
  const overlays = previous?.layers.filter(layer => layerIds.includes(layer.id)) || [];
  return {
    ...next,
    sources,
    layers: [...next.layers.filter(layer => !layerIds.includes(layer.id)), ...overlays] as LayerSpecification[],
  };
}

/** Browser-only lifecycle, started by MapComponent after its initial map has loaded. */
export class MapStyleController {
  public loading = false;
  private version = 0;
  private destroyed = false;
  private request?: AbortController;
  private requestTimeout?: ReturnType<typeof setTimeout>;
  private swapTimeout?: ReturnType<typeof setTimeout>;
  private readyListener?: () => void;
  private errorListener?: (event: any) => void;
  private confirmedStyle?: StyleSpecification;

  constructor(
    private readonly map: Map,
    private mode: MapMode,
    private readonly sourceIds: readonly string[],
    private readonly layerIds: readonly string[],
    private readonly beforeSwap: () => void,
    private readonly settled: () => void,
    private readonly failed: () => void,
  ) {}

  public async setMode(mode: MapMode): Promise<void> {
    if (this.destroyed) return;
    const version = ++this.version;
    this.cancelRequest();
    if (mode === this.mode && !this.loading) return;

    const request = this.request = new AbortController();
    this.requestTimeout = setTimeout(() => {
      if (version === this.version) {
        this.cancelRequest();
        this.fail(version);
      }
    }, 8000);
    try {
      // Stage the small style document first. A failed request leaves the displayed map untouched.
      const response = await fetch(MAP_PROVIDER_CONFIG.styleUrls[mode], {signal: request.signal});
      if (!response.ok) throw new Error('Map style request failed');
      const style: StyleSpecification = await response.json();
      if (style?.version !== 8 || !style.sources || !Array.isArray(style.layers)) {
        throw new Error('Invalid map style');
      }
      if (version !== this.version || this.destroyed || request.signal.aborted) return;
      this.cancelRequest();
      this.swap(style, mode, version);
    } catch {
      if (version === this.version && !this.destroyed && !request.signal.aborted) {
        this.cancelRequest();
        this.fail(version);
      }
    }
  }

  public destroy(): void {
    this.destroyed = true;
    ++this.version;
    this.cancelRequest();
    this.clearSwapListeners();
  }

  private swap(style: StyleSpecification, mode: MapMode, version: number): void {
    const previous = this.map.getStyle();
    if (!this.loading) this.confirmedStyle = previous;
    this.loading = true;
    this.beforeSwap();
    const next = preserveMapLayers(previous || this.confirmedStyle, style, this.sourceIds, this.layerIds);
    this.apply(next, version, () => {
      this.mode = mode;
      this.finish();
    }, () => this.fail(version));
  }

  private apply(style: StyleSpecification, version: number, ready: () => void, failed?: () => void): void {
    this.clearSwapListeners();
    let armed = false;
    let styleError = false;
    this.readyListener = () => {
      if (armed && !styleError && version === this.version && !this.destroyed && this.map.isStyleLoaded()) {
        this.clearSwapListeners();
        ready();
      }
    };
    this.errorListener = (event) => {
      // Ordinary tile/source retries do not mean the style document has failed.
      if (event.sourceId || event.tile || !failed) return;
      styleError = true;
      void Promise.resolve().then(() => {
        if (version === this.version && !this.destroyed && this.loading) failed();
      });
    };
    this.map.on('styledata', this.readyListener);
    this.map.on('idle', this.readyListener);
    this.map.on('error', this.errorListener);
    if (failed) this.swapTimeout = setTimeout(() => {
      if (version === this.version && !this.destroyed) failed();
    }, 8000);
    try {
      // A diff retains shared vector sources. Unlike style.load, styledata/idle also
      // cover a successful diff which does not reconstruct MapLibre's style object.
      // Explicit camera defaults also protect an untouched camera on a full rebuild.
      const center = this.map.getCenter();
      this.map.setStyle({
        ...style, center: [center.lng, center.lat], zoom: this.map.getZoom(),
        bearing: this.map.getBearing(), pitch: this.map.getPitch(),
      });
      armed = true;
      this.readyListener?.();
    } catch {
      failed?.();
    }
  }

  private fail(version: number): void {
    if (version !== this.version || this.destroyed) return;
    const restoreVersion = ++this.version;
    this.cancelRequest();
    this.clearSwapListeners();
    if (this.loading && this.confirmedStyle) {
      // Restore the last confirmed style, including overlays, without touching DOM
      // markers, popups or the camera. Pending component updates are flushed afterwards.
      this.apply(this.confirmedStyle, restoreVersion, () => this.finish());
    }
    this.failed();
  }

  private finish(): void {
    this.loading = false;
    this.confirmedStyle = undefined;
    this.settled();
  }

  private cancelRequest(): void {
    this.request?.abort();
    this.request = undefined;
    clearTimeout(this.requestTimeout);
    this.requestTimeout = undefined;
  }

  private clearSwapListeners(): void {
    if (this.readyListener) {
      this.map.off('styledata', this.readyListener);
      this.map.off('idle', this.readyListener);
    }
    if (this.errorListener) this.map.off('error', this.errorListener);
    this.readyListener = undefined;
    this.errorListener = undefined;
    clearTimeout(this.swapTimeout);
    this.swapTimeout = undefined;
  }
}
